import React, { useState, useMemo } from 'react';
import {
  Meal,
  Symptom,
  WeeklyInsight,
  MealType,
} from '../lib/types';
import {
  ShieldCheck,
  Search,
  QrCode,
  Camera,
  Sparkles,
  Check,
  X,
  ArrowRight,
  Info,
  Utensils,
  ChevronRight,
  Layers,
} from 'lucide-react';
import {
  searchFodmapDatabase,
  matchIngredientToFodmap,
  getSafePortionBrackets,
  FodmapFoodItem,
} from '../lib/fodmapEngine';

interface ActionsViewProps {
  userId?: string | null;
  meals: Meal[];
  symptoms: Symptom[];
  insights: WeeklyInsight[];
  onOpenLogMeal?: (mealType?: MealType) => void;
  onOpenLogPoop?: () => void;
  onUpdateMeal?: (meal: Meal) => Promise<unknown> | void;
  onOpenFodmapScanner?: (mode?: 'search' | 'barcode' | 'photo') => void;
}

const COMMON_STAPLE_QUICK_CHECKS = [
  'Bananas',
  'Avocado',
  'Rolled Oats',
  'Sourdough Bread',
  'Garlic-Infused Olive Oil',
  'Greek Yogurt',
  'Oyster Mushrooms',
  'Firm Tofu',
  'Almonds',
  'Sweet Potato',
  'Cheddar Cheese',
  'Spring Onion Green Tops',
];

const POPULAR_SAFE_SWAPS = [
  {
    high: 'Garlic & Garlic Powder',
    highRating: 'high' as const,
    highCulprit: 'Fructans',
    low: 'Garlic-Infused Olive Oil',
    lowRating: 'low' as const,
    tip: 'Fructans are water-soluble but not oil-soluble. Garlic infused in pure oil is 100% certified Low FODMAP.',
  },
  {
    high: 'Onion & Shallots',
    highRating: 'high' as const,
    highCulprit: 'Fructans',
    low: 'Spring Onion (Green Tops Only)',
    lowRating: 'low' as const,
    tip: 'The dark green leaf tops contain zero fructans, whereas white bulbs are high FODMAP.',
  },
  {
    high: 'Button / Cremini Mushrooms',
    highRating: 'high' as const,
    highCulprit: 'Mannitol',
    low: 'Oyster Mushrooms or Canned Champignons',
    lowRating: 'low' as const,
    tip: 'Oyster mushrooms contain negligible mannitol (safe up to 75g per sitting).',
  },
  {
    high: 'Wheat Pasta & Bread',
    highRating: 'high' as const,
    highCulprit: 'Fructans',
    low: 'Gluten-Free Grain / Traditional Sourdough',
    lowRating: 'low' as const,
    tip: 'Long slow fermentation in authentic sourdough digests fructans, making 1-2 slices gut safe.',
  },
  {
    high: "Cow's Whole Milk",
    highRating: 'high' as const,
    highCulprit: 'Lactose',
    low: 'Lactose-Free Milk or Almond Milk',
    lowRating: 'low' as const,
    tip: 'Lactase enzyme breaks down lactose sugars completely into easily absorbed simple glucose and galactose.',
  },
];

export const ActionsView: React.FC<ActionsViewProps> = React.memo(({
  onOpenFodmapScanner,
}) => {
  const [inlineQuery, setInlineQuery] = useState('');
  const [selectedFood, setSelectedFood] = useState<FodmapFoodItem | null>(null);

  const searchResults = useMemo(() => {
    if (!inlineQuery.trim()) return [];
    return searchFodmapDatabase(inlineQuery.trim(), 8);
  }, [inlineQuery]);

  const selectedFoodBrackets = useMemo(() => {
    if (!selectedFood) return null;
    return getSafePortionBrackets(selectedFood);
  }, [selectedFood]);

  return (
    <div className="flex flex-col gap-4 pb-28 pt-1 text-left select-none max-w-md mx-auto">
      {/* ========================================================================= */}
      {/* 1. LOW FODMAP CHECKER & SCANNER HUB (Primary Action Hero)                 */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-linear-to-br from-emerald-500/10 via-teal-500/5 to-transparent border border-emerald-500/30 dark:border-emerald-500/20 shadow-xs relative overflow-hidden transition-all">
        <div className="flex items-center justify-between pb-3 border-b border-emerald-500/20">
          <div className="flex items-center space-x-2.5">
            <div className="w-9 h-9 rounded-2xl bg-emerald-500 text-white flex items-center justify-center shadow-md shadow-emerald-500/20 shrink-0">
              <ShieldCheck className="w-5 h-5 stroke-[2.5]" />
            </div>
            <div>
              <h2 className="text-base font-black text-slate-950 dark:text-white tracking-tight">
                Low FODMAP Checker
              </h2>
              <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                Clinical Monash University Standard
              </p>
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-600 dark:text-zinc-300 font-medium my-3 leading-relaxed">
          Check any ingredient, meal, or packaged grocery item for fermentable oligosaccharides, disaccharides, monosaccharides, and polyols before eating.
        </p>

        {/* 3 Quick Launch Scanner Buttons */}
        <div className="grid grid-cols-3 gap-2 pt-1">
          <button
            type="button"
            onClick={() => onOpenFodmapScanner?.('search')}
            className="p-3 rounded-2xl bg-white dark:bg-[#1A1D24] border border-slate-200/80 dark:border-zinc-800 hover:border-emerald-500 flex flex-col items-center justify-center space-y-1.5 group active:scale-95 transition-all shadow-2xs"
          >
            <Search className="w-5 h-5 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
            <span className="text-xs font-bold text-slate-900 dark:text-white">Database</span>
          </button>

          <button
            type="button"
            onClick={() => onOpenFodmapScanner?.('barcode')}
            className="p-3 rounded-2xl bg-white dark:bg-[#1A1D24] border border-slate-200/80 dark:border-zinc-800 hover:border-emerald-500 flex flex-col items-center justify-center space-y-1.5 group active:scale-95 transition-all shadow-2xs"
          >
            <QrCode className="w-5 h-5 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
            <span className="text-xs font-bold text-slate-900 dark:text-white">Scan Barcode</span>
          </button>

          <button
            type="button"
            onClick={() => onOpenFodmapScanner?.('photo')}
            className="p-3 rounded-2xl bg-white dark:bg-[#1A1D24] border border-slate-200/80 dark:border-zinc-800 hover:border-emerald-500 flex flex-col items-center justify-center space-y-1.5 group active:scale-95 transition-all shadow-2xs"
          >
            <Camera className="w-5 h-5 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
            <span className="text-xs font-bold text-slate-900 dark:text-white">Plate OCR</span>
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 2. DIRECT INSTANT FOOD LOOKUP                                             */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Search className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
            <h3 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
              Instant Food Rating Lookup
            </h3>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            0ms Local Engine
          </span>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-zinc-500" />
          <input
            type="text"
            value={inlineQuery}
            onChange={(e) => setInlineQuery(e.target.value)}
            placeholder="Type any food (e.g. Avocado, Garlic, Sourdough, Oats)..."
            className="w-full h-11 pl-10 pr-9 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs font-medium text-slate-900 dark:text-zinc-100 placeholder-slate-400 focus:outline-none focus:border-emerald-500 transition-colors"
          />
          {inlineQuery && (
            <button
              type="button"
              onClick={() => setInlineQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-white p-1"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Live Search Results Dropdown */}
        {searchResults.length > 0 && (
          <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1 pt-1">
            {searchResults.map((food) => {
              const ratingColor =
                food.rating === 'low'
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
                  : food.rating === 'moderate'
                  ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30'
                  : 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30';

              return (
                <button
                  key={food.id}
                  type="button"
                  onClick={() => setSelectedFood(food)}
                  className="w-full p-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 dark:bg-zinc-900 dark:hover:bg-zinc-800/80 border border-slate-200/60 dark:border-zinc-800 text-left transition-colors flex items-center justify-between"
                >
                  <div className="truncate pr-2">
                    <div className="text-xs font-bold text-slate-900 dark:text-zinc-100 truncate">
                      {food.name}
                    </div>
                    <div className="text-[10px] text-slate-500 dark:text-zinc-400 truncate mt-0.5">
                      {food.category} • {food.servings?.standard?.title || (food.safeGrams ? `Safe: ${food.safeGrams}g` : 'Standard serving')}
                      {food.primaryCulprit ? ` • ${food.primaryCulprit}` : ''}
                    </div>
                  </div>
                  <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border shrink-0 ${ratingColor}`}>
                    {food.rating}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Quick Staple Tags */}
        {!inlineQuery && (
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 block mb-2">
              Common Everyday Staples
            </span>
            <div className="flex flex-wrap gap-1.5">
              {COMMON_STAPLE_QUICK_CHECKS.map((staple) => (
                <button
                  key={staple}
                  type="button"
                  onClick={() => {
                    const match = matchIngredientToFodmap(staple).matchedFood;
                    if (match) setSelectedFood(match);
                    else setInlineQuery(staple);
                  }}
                  className="px-2.5 py-1 rounded-xl text-[11px] font-semibold bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700 border border-slate-200/50 dark:border-zinc-700/60 transition-colors"
                >
                  {staple}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* 3. SMART LOW FODMAP SWAP DIRECTORY                                        */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
              Essential Safe Swaps
            </h3>
          </div>
          <span className="text-[10px] font-bold uppercase text-slate-400">
            Culinary Substitutes
          </span>
        </div>

        <div className="space-y-3">
          {POPULAR_SAFE_SWAPS.map((swap, idx) => (
            <div
              key={idx}
              onClick={() => {
                const match = matchIngredientToFodmap(swap.low).matchedFood;
                if (match) setSelectedFood(match);
              }}
              className="p-3.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/70 border border-slate-200/60 dark:border-zinc-800 hover:border-emerald-500/50 dark:hover:border-emerald-500/40 transition-all cursor-pointer space-y-2.5 active:scale-[0.99] group shadow-2xs"
            >
              {/* High Row */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-2 min-w-0">
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase bg-rose-100 dark:bg-rose-950/70 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900/40 shrink-0">
                    High
                  </span>
                  <span className="text-xs font-semibold text-slate-700 dark:text-zinc-300 line-through opacity-85">
                    {swap.high}
                  </span>
                </div>
                {swap.highCulprit && (
                  <span className="text-[10px] font-semibold text-rose-600 dark:text-rose-400 shrink-0">
                    {swap.highCulprit}
                  </span>
                )}
              </div>

              {/* Safe Swap Row */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-2 min-w-0">
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/40 shrink-0">
                    Safe
                  </span>
                  <span className="text-xs font-black text-emerald-700 dark:text-emerald-400 group-hover:underline">
                    {swap.low}
                  </span>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all shrink-0" />
              </div>

              {/* Monash Rationale Tip */}
              <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-relaxed pt-1.5 border-t border-slate-200/50 dark:border-zinc-800/60">
                {swap.tip}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 4. FOOD DETAIL INSPECTION MODAL                                           */}
      {/* ========================================================================= */}
      {selectedFood && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-sm bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-3xl p-5 shadow-2xl space-y-4 text-slate-900 dark:text-zinc-100">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-black text-slate-950 dark:text-white">
                  {selectedFood.name}
                </h3>
                <span className="text-xs text-slate-500 dark:text-zinc-400 capitalize">
                  {selectedFood.category}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setSelectedFood(null)}
                className="p-1.5 rounded-full bg-slate-100 dark:bg-zinc-800 text-slate-500 hover:text-slate-900 dark:hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* FODMAP Rating Badge & Primary Culprit */}
            <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200/80 dark:border-zinc-800 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-slate-500 dark:text-zinc-400">
                  Monash FODMAP Rating
                </span>
                <span
                  className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border ${
                    selectedFood.rating === 'low'
                      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
                      : selectedFood.rating === 'moderate'
                      ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30'
                      : 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30'
                  }`}
                >
                  {selectedFood.rating} FODMAP
                </span>
              </div>

              {selectedFood.primaryCulprit && (
                <div className="text-xs text-slate-700 dark:text-zinc-300">
                  <span className="text-slate-400 dark:text-zinc-500">Primary Culprit: </span>
                  <span className="font-bold">{selectedFood.primaryCulprit}</span>
                </div>
              )}

              {selectedFood.notes && (
                <div className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed pt-1 border-t border-slate-200/60 dark:border-zinc-800">
                  {selectedFood.notes}
                </div>
              )}
            </div>

            {/* Safe Portion Serving Guide */}
            {selectedFoodBrackets && (
              <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200/80 dark:border-zinc-800 space-y-2">
                <div className="text-[10px] font-bold uppercase text-slate-500 dark:text-zinc-400">
                  Portion Guide & Safe Limits
                </div>
                <div className="text-xs font-semibold text-slate-900 dark:text-zinc-100 flex items-center justify-between">
                  <span>Safe Portion:</span>
                  <span className="font-mono text-emerald-700 dark:text-emerald-400 font-bold">
                    {selectedFoodBrackets.safeRange}
                  </span>
                </div>
                {selectedFoodBrackets.moderateRange !== 'N/A' && (
                  <div className="text-xs font-medium text-slate-600 dark:text-zinc-400 flex items-center justify-between pt-1">
                    <span>Moderate FODMAP:</span>
                    <span className="font-mono text-amber-700 dark:text-amber-400 font-bold">
                      {selectedFoodBrackets.moderateRange}
                    </span>
                  </div>
                )}
                {selectedFoodBrackets.highRange !== 'N/A' && (
                  <div className="text-xs font-medium text-slate-600 dark:text-zinc-400 flex items-center justify-between pt-1">
                    <span>High FODMAP Limit:</span>
                    <span className="font-mono text-rose-700 dark:text-rose-400 font-bold">
                      {selectedFoodBrackets.highRange}
                    </span>
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => setSelectedFood(null)}
              className="w-full py-2.5 rounded-xl bg-slate-950 dark:bg-white text-white dark:text-black font-bold text-xs"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
});
